import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { authApi } from "../../api";
import { createForgotPasswordSchema } from "../schemas/forgot-password.schema";
import type {
  ForgotPasswordFormMethods,
  ForgotPasswordFormValues,
} from "../types/forgot-password.types";

interface UseForgotPasswordReturn {
  submitForgotPassword: (email: string) => Promise<void>;
  loading: boolean;
  error: string | null;
  retryAfterSeconds: number | null;
  form: ForgotPasswordFormMethods;
}

const extractRetryAfterSeconds = (message: string | undefined | null): number | null => {
  if (!message) return null;

  const match = message.match(/(\d+)\s*seconds?/i);
  if (match?.[1]) {
    const seconds = Number(match[1]);
    return Number.isFinite(seconds) && seconds > 0 ? seconds : null;
  }

  return null;
};

const formatRetryMessage = (message: string, seconds: number) =>
  message.replace(/\d+\s*seconds?/i, `${seconds} second${seconds === 1 ? "" : "s"}`);

export const useForgotPassword = (): UseForgotPasswordReturn => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cooldownMessage, setCooldownMessage] = useState<string | null>(null);
  const [retryAfterSeconds, setRetryAfterSeconds] = useState<number | null>(null);
  const forgotPasswordSchema = createForgotPasswordSchema(t);

  const form = useForm<ForgotPasswordFormValues>({
    resolver: zodResolver(forgotPasswordSchema),
    mode: "onTouched",
    reValidateMode: "onChange",
    defaultValues: {
      email: "",
    },
  });

  const submitForgotPassword = useCallback(
    async (email: string) => {
      setLoading(true);
      setError(null);
      setCooldownMessage(null);
      setRetryAfterSeconds(null);

      try {
        const response = await authApi.sendResetPasswordLink({ email });

        if (response) {
          navigate("/password-link-sent", { state: { email } });
          return;
        }

      } catch (err: any) {
        const responseMessage = err?.message;
        const retrySeconds = extractRetryAfterSeconds(responseMessage);

        if (retrySeconds) {
          const retryMessage = responseMessage || t("auth.request_reset_failed");
          setCooldownMessage(retryMessage);
          setRetryAfterSeconds(retrySeconds);
          setError(formatRetryMessage(retryMessage, retrySeconds));
        } else if (responseMessage) {
          setError(responseMessage);
        } else if (err?.response?.status === 404) {
          setError(t("auth.email_not_found"));
        } else if (err?.message === "Network Error") {
          setError(t("auth.check_internet_connection"));
        } else {
          setError(t("auth.request_reset_failed"));
        }

        throw err;
      } finally {
        setLoading(false);
      }
    },
    [navigate, t],
  );

  useEffect(() => {
    if (retryAfterSeconds === null) {
      return;
    }

    if (retryAfterSeconds <= 0) {
      setRetryAfterSeconds(null);
      setCooldownMessage(null);
      return;
    }

    const timer = window.setInterval(() => {
      setRetryAfterSeconds((current) => {
        if (current === null) {
          return null;
        }

        if (current <= 1) {
          window.clearInterval(timer);
          setCooldownMessage(null);
          return null;
        }

        return current - 1;
      });
    }, 1000);

    return () => {
      window.clearInterval(timer);
    };
  }, [retryAfterSeconds]);

  const displayError =
    retryAfterSeconds !== null && cooldownMessage
      ? formatRetryMessage(cooldownMessage, retryAfterSeconds)
      : error;

  return {
    submitForgotPassword,
    loading,
    error: displayError,
    retryAfterSeconds,
    form,
  };
};
